import { useEffect, useRef, useState, useCallback } from "react";
import useRouter from "./hooks/useRouter";
import useAuth from "./hooks/useAuth";
import { configureApi, apiFetch } from "./api";
import { HomePage, OnboardingPage, SignupPage, LoginPage, WelcomePage, VerifyEmailPage, AdminChatPage } from "./pages";

const OB_KEY = "onboarding_data";

export default function App() {
  const { page, navigate } = useRouter();
  const { token, isAuthenticated, email, role, login, logout } = useAuth();
  const prevToken = useRef(token);
  const [onboardingDone, setOnboardingDone] = useState(null); // null = loading

  // Check onboarding status for authenticated users
  const checkOnboarding = useCallback(async () => {
    if (!isAuthenticated) { setOnboardingDone(null); return; }
    try {
      const res = await apiFetch("/api/v1/profile");
      if (res.ok) {
        const data = await res.json();
        setOnboardingDone(data.onboarding_completed);
      }
    } catch {
      setOnboardingDone(true); // fallback: don't block
    }
  }, [isAuthenticated]);

  useEffect(() => { checkOnboarding(); }, [checkOnboarding]);

  useEffect(() => {
    // If token existed but is now invalid (expired/removed), redirect to home
    if (prevToken.current && !isAuthenticated) {
      logout();
      navigate("/");
    }
    prevToken.current = token;
  }, [token, isAuthenticated, logout, navigate]);

  useEffect(() => {
    configureApi({
      onUnauthorized: () => {
        logout();
        navigate("/login");
      },
    });
  }, [logout, navigate]);

  useEffect(() => {
    if (page === "home" && isAuthenticated) {
      navigate("/dashboard");
    }
  }, [page, isAuthenticated, navigate]);

  // Existing user hasn't completed onboarding → redirect
  useEffect(() => {
    if (isAuthenticated && onboardingDone === false && page === "dashboard") {
      navigate("/onboarding");
    }
  }, [isAuthenticated, onboardingDone, page, navigate]);

  if (page === "home") {
    if (isAuthenticated) return null;
    return (
      <HomePage
        onSignup={() => navigate("/onboarding")}
        onLogin={() => navigate("/login")}
      />
    );
  }

  if (page === "onboarding") {
    // Existing user who hasn't completed onboarding
    if (isAuthenticated && onboardingDone === false) {
      return (
        <OnboardingPage
          onComplete={async (data) => {
            try {
              await apiFetch("/api/v1/profile/onboarding", {
                method: "PATCH",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                  country_of_origin: data.country,
                  swedish_level: data.level,
                  referral_source: data.referral,
                }),
              });
            } catch { /* best effort */ }
            setOnboardingDone(true);
            navigate("/dashboard");
          }}
        />
      );
    }
    // New user (not authenticated) → collect data, then go to signup
    if (!isAuthenticated) {
      return (
        <OnboardingPage
          onComplete={(data) => {
            localStorage.setItem(OB_KEY, JSON.stringify(data));
            navigate("/signup");
          }}
        />
      );
    }
    // Authenticated + onboarding done → dashboard
    navigate("/dashboard");
    return null;
  }

  if (page === "signup") {
    if (isAuthenticated) return null;
    return (
      <SignupPage
        onSuccess={(data) => {
          localStorage.removeItem(OB_KEY);
          login(data.access_token);
          setOnboardingDone(true);
          navigate("/welcome");
        }}
        onBack={() => navigate("/onboarding")}
        onLogin={() => navigate("/login")}
      />
    );
  }

  if (page === "welcome") {
    if (!isAuthenticated) return null;
    return <WelcomePage onContinue={() => navigate("/dashboard")} />;
  }

  if (page === "verify-email") {
    return (
      <VerifyEmailPage
        onGoHome={() => navigate(isAuthenticated ? "/dashboard" : "/")}
      />
    );
  }

  if (page === "login" || (page === "dashboard" && !isAuthenticated)) {
    return (
      <LoginPage
        onSuccess={(data) => {
          login(data.access_token);
          checkOnboarding();
          navigate("/dashboard");
        }}
        onBack={() => navigate("/")}
        onSignup={() => navigate("/onboarding")}
      />
    );
  }

  return (
    <AdminChatPage
      userEmail={email}
      userRole={role}
      onLogout={() => {
        logout();
        setOnboardingDone(null);
        navigate("/");
      }}
    />
  );
}
